import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { Sparkles, Mail, Lock, Loader2 } from 'lucide-react';

// Google Icon SVG component
const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
);

export default function Login() {
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showResetForm, setShowResetForm] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSent, setResetSent] = useState(false);
    const { login, isLoading, error, clearError } = useAuthStore();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearError();

        try {
            await login(email, password);
            // Navigate to dashboard - ProtectedRoute will redirect to onboarding if needed
            navigate('/dashboard');
        } catch {
            // Error is handled by store
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            // Call password reset API
            await fetch('/api/v1/auth/password-reset/request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail })
            });
            setResetSent(true);
        } catch {
            // Always show success to prevent email enumeration
            setResetSent(true);
        }
    };

    if (showResetForm) {
        return (
            <div>
                {/* Mobile logo */}
                <div className="lg:hidden flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xl font-bold">aiOps</span>
                </div>

                <h2 className="text-3xl font-bold mb-2">Reset Password</h2>
                <p className="text-dark-400 mb-8">
                    {resetSent
                        ? "Check your email for reset instructions"
                        : "Enter your email to receive a reset link"}
                </p>

                {!resetSent ? (
                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <div>
                            <label className="label">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
                                <input
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    className="input pl-10"
                                    placeholder="you@example.com"
                                    required
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-full"
                        >
                            Send Reset Link
                        </button>
                    </form>
                ) : (
                    <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 mb-4">
                        If an account exists with that email, you'll receive reset instructions shortly.
                    </div>
                )}

                <button
                    onClick={() => { setShowResetForm(false); setResetSent(false); }}
                    className="mt-6 text-center text-primary-400 hover:text-primary-300 w-full"
                >
                    ← Back to Sign in
                </button>
            </div>
        );
    }

    return (
        <div>
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">aiOps</span>
            </div>

            <h2 className="text-3xl font-bold mb-2">Welcome back</h2>
            <p className="text-dark-400 mb-8">Sign in to access your AI ecosystem</p>

            {/* OAuth Error from redirect */}
            {searchParams.get('error') && (
                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                    {searchParams.get('error') === 'oauth_denied'
                        ? 'Sign in was cancelled'
                        : 'Google sign in failed. Please try again.'}
                </div>
            )}

            {error && (
                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                    {error}
                </div>
            )}

            {/* Google Sign In Button */}
            <a
                href={`${import.meta.env.VITE_API_URL || 'https://ai-operations-assistant.anikchowdhury.workers.dev/api/v1'}/auth/google`}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-lg transition-colors mb-6"
            >
                <GoogleIcon />
                Continue with Google
            </a>

            <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-dark-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-dark-900 text-dark-500">or sign in with email</span>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="label">Email</label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input pl-10"
                            placeholder="you@example.com"
                            required
                        />
                    </div>
                </div>

                <div>
                    <label className="label">Password</label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input pl-10"
                            placeholder="••••••••"
                            required
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="btn btn-primary w-full flex items-center justify-center gap-2"
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Signing in...
                        </>
                    ) : (
                        'Sign in'
                    )}
                </button>
            </form>

            <div className="mt-4 text-center">
                <button
                    onClick={() => setShowResetForm(true)}
                    className="text-sm text-dark-400 hover:text-primary-400 transition-colors"
                >
                    Forgot your password?
                </button>
            </div>

            <p className="mt-6 text-center text-dark-400">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary-400 hover:text-primary-300">
                    Start free trial
                </Link>
            </p>
        </div>
    );
}
